/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Animation, Colors, Link, Typography } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { useUniversalNavContext } from '../../../../hooks/useUniversalNavContext';
import type { UniversalNavLinkType } from '../../../../constants';
import { emitUserInteractionEvent } from '../../../../utils/avroEvents';
import { ArrowLinkIcon } from '../..';

type Props = {
  /** List of links in the MegaNav */
  submenu: UniversalNavLinkType[];
  /** Name of the parent nav item to be included in tracking event payloads */
  parent?: string;
  /** Function called when closing menu on a link from within the MegaMenu */
  onCloseMenu?: () => void;
};

const Styles = labelStyles('SubMenuList', {
  root: css({
    display: 'flex',
    flexDirection: 'column',
  }),
  item: {
    marginBottom: '10px',
    ...Typography.paragraph3,
    '&:last-child': {
      marginBottom: 0,
    },
    '&:focus-within': {
      outline: `1px solid ${Colors.blue}`,
    },
  },
  link: css({
    color: Colors.neutral700,
    ...Typography.paragraph3,
    position: 'relative',
    textDecoration: 'none',
    transition: `text-decoration 100ms ${Animation.transitionTimingFunction}`,
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:hover > span': {
      marginLeft: 4,
      opacity: 1,
    },
    '&:focus': {
      outline: 'none',
    },
  }),
});

/**
 * @private
 * List of links inside the MegaMenu
 */
export const SubMenuList = (props: Props) => {
  const { submenu } = props;
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const emitEvent = (link: UniversalNavLinkType, eventType: string) => {
    emitUserInteractionEvent({
      event_action: eventType,
      interaction_name: `universal_nav_submenu_link_${eventType}`,
      option_selected: link.text,
      interaction_raw_text: link.text,
      destination_href: link.href ?? '',
      custom_tracking_fields: { parent: props.parent },
    });
  };

  const handleLinkClick = (link: UniversalNavLinkType) => {
    props.onCloseMenu?.();
    emitEvent(link, 'click');
  };

  const handleLinkHover = (link: UniversalNavLinkType) => {
    emitEvent(link, 'hover');
  };

  return (
    <ul css={Styles.root} data-zds>
      {submenu.map((link, index) => (
        <li css={Styles.item} key={`${link.text}-${index}`} data-zds>
          <Link
            css={Styles.link}
            color={null}
            href={getFullyQualifiedURL(link.href) || undefined}
            onClick={() => handleLinkClick(link)}
            onMouseEnter={() => handleLinkHover(link)}
            target={link.target}
          >
            {link.text}
            <ArrowLinkIcon />
          </Link>
        </li>
      ))}
    </ul>
  );
};
